import { useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { US_STATES } from "@/config/geo/us-states";
import { detectDomesticNumber, isPotentiallyDomestic } from "@/lib/domestic-number-detection";
import { useCallerCountry } from "@/hooks/useCallerCountry";

export interface CountryRate {
  id: string;
  countryCode: string;
  countryName: string;
  flag: string;
  destinationType: string;
  ratePerMinuteCents: number;
  retailRateCents: number;
  discountPercent: number;
  lebaraEstimateCents?: number;
  savingsPercent?: number;
  qualityScore?: number;
  billingIncrement?: string; // e.g. '60/60', '6/6', '1/1'
  isGstInclusive?: boolean;
  currency?: string;
  isUnlimited?: boolean;
  /** false when the destination is on the blocked list (not offered yet). */
  available?: boolean;
  /** "Coming soon" for destinations we don't connect yet. */
  label?: string;
}

interface RatesResponse {
  success: boolean;
  userType: string;
  popular: CountryRate[];
  all: CountryRate[];
  lastUpdated: string;
  currency: string;
  isGstInclusive: boolean;
  gstRate: number;
}

export function useCountryRates(options: { currency?: "AUD" | "USD" } = {}) {
  const requestedCurrency = options.currency ?? "AUD";
  const {
    callerCountry,
    displayCurrency: callerDisplayCurrency,
    currencySymbol,
    currencyName,
    showGst,
    formatLocalRate,
    formatLocalAmount,
    isLoading: geoLoading,
  } = useCallerCountry();

  const { data, isLoading, error } = useQuery({
    // v6: bust persisted caches after 2026-07-05 rate rebuild + Option A
    // rewire. Older keys (v5-usd-base) may still be persisted in browser
    // IndexedDB and render pre-rebuild numbers (e.g. A$0.68 for South Sudan
    // when voice_routes now holds 0.47 fixed / 0.77 mobile). Bumping the
    // key forces a clean refetch on every client.
    queryKey: ["country-rates", "v6-2026-07-05", requestedCurrency],

    queryFn: async (): Promise<RatesResponse> => {
      const { data, error } = await supabase.functions.invoke("elefa-connect-rates", { body: { currency: requestedCurrency } });

      if (error) {
        console.error("Rates fetch error:", error);
        throw error;
      }

      return data as RatesResponse;
    },
    // Rates change daily via the ASTPP push; 5 min matches the edge function's
    // own cache TTL. Persisted data still renders instantly, then revalidates.
    staleTime: 5 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
  const { allExpanded, popularExpanded } = useMemo(() => {
    const basePopular = data?.popular || [];
    const baseAll = data?.all || [];

    const us = baseAll.find((r) => r.countryCode === "1");

    if (!us) {
      return { allExpanded: baseAll, popularExpanded: basePopular };
    }

    const usAll: CountryRate = {
      ...us,
      id: `${us.id}-US-ALL`,
      // ASTPP publishes a single "+1" row shared by US + Canada + NANP
      // territories. Label the tile honestly; slug lookup falls back to
      // United States so the existing country detail page keeps working.
      countryName: "United States & Canada",
      destinationType: "country",
    };

    const usStates: CountryRate[] = US_STATES.map((s) => ({
      ...us,
      id: `${us.id}-US-${s.code}`,
      countryName: `United States - ${s.name}`,
      destinationType: `us_state:${s.code}`,
    }));

    const withoutUS = baseAll.filter((r) => r.id !== us.id);

    return {
      // put US at the top for easy testing
      allExpanded: [usAll, ...usStates, ...withoutUS],
      popularExpanded: [usAll, ...basePopular.filter((r) => r.id !== us.id)],
    };
  }, [data?.all, data?.popular]);

  const getRateForCountry = useCallback((countryCode: string): CountryRate | undefined => {
    return allExpanded.find((r) => r.countryCode === countryCode);
  }, [allExpanded]);

  const countryCodesDesc = useMemo(() => {
    const codes = Array.from(new Set(allExpanded.map((r) => r.countryCode).filter(Boolean)));
    // Longest-prefix match first: 3-digit codes before 2-digit before 1-digit
    return codes.sort((a, b) => b.length - a.length || b.localeCompare(a));
  }, [allExpanded]);

  const detectCountryFromPhone = useCallback((rawPhone: string): CountryRate | undefined => {
    const raw = (rawPhone || "").trim();
    if (!raw) return undefined;

    // If data hasn't loaded yet, return undefined
    if (countryCodesDesc.length === 0) {
      return undefined;
    }

    // Normalize the input
    let cleaned = raw.replace(/[^\d+]/g, "");
    
    // Handle 00 prefix (European international format)
    if (cleaned.startsWith("00")) {
      cleaned = "+" + cleaned.slice(2);
    }
    
    // Handle 011 prefix (US international format)
    if (cleaned.startsWith("011")) {
      cleaned = "+" + cleaned.slice(3);
    }

    // Keep digits only for matching
    const digits = cleaned.startsWith("+") ? cleaned.slice(1) : cleaned;
    
    // Need at least 1 digit to attempt matching
    if (digits.length < 1) return undefined;

    // PRIORITY 0: Check for domestic patterns FIRST (before international codes)
    // Only check if the number looks domestic (no + prefix, no international prefix)
    if (!raw.startsWith("+") && !raw.startsWith("00") && !raw.startsWith("011")) {
      const domesticResult = detectDomesticNumber(digits);
      if (domesticResult) {
        return allExpanded.find((r) => r.countryCode === domesticResult.countryCode);
      }
    }

    // PRIORITY 1: Exact prefix match (user typed full country code + more digits)
    for (const code of countryCodesDesc) {
      if (code && digits.startsWith(code)) {
        return allExpanded.find((r) => r.countryCode === code);
      }
    }

    // PRIORITY 2: Partial match (user is still typing the country code)
    // Only for 1-3 digits (still in country code phase)
    if (digits.length <= 3) {
      for (const code of countryCodesDesc) {
        if (code && code.startsWith(digits)) {
          return allExpanded.find((r) => r.countryCode === code);
        }
      }
    }

    return undefined;
  }, [countryCodesDesc, allExpanded]);

  const searchCountries = useCallback((query: string): CountryRate[] => {
    if (!query || allExpanded.length === 0) return popularExpanded;

    const lowerQuery = query.toLowerCase();
    return allExpanded.filter(
      (r) => r.countryName.toLowerCase().includes(lowerQuery) || r.countryCode.includes(query)
    );
  }, [allExpanded, popularExpanded]);

  // Localized rate formatter: converts AUD cents to local currency string
  // The response names its own currency. The request is a preference the
  // server may not honour yet, so the label follows the data, never the ask.
  const responseCurrency: "AUD" | "USD" = data?.currency === "USD" ? "USD" : "AUD";
  const formatRate = useCallback((cents: number): string => {
    return responseCurrency === "USD" ? `US$${(cents / 100).toFixed(2)}` : formatLocalRate(cents);
  }, [formatLocalRate, responseCurrency]);

  return {
    popular: popularExpanded,
    all: allExpanded,
    userType: data?.userType || "connect_only",
    currency: responseCurrency,
    currencySymbol,
    currencyName,
    displayCurrency: responseCurrency,
    showGst,
    isGstInclusive: showGst, // GST only relevant for AU callers
    gstRate: data?.gstRate || 10,
    isLoading: isLoading || geoLoading,
    error,
    getRateForCountry,
    detectCountryFromPhone,
    searchCountries,
    formatRate,
    formatLocalAmount,
    callerCountry,
  };
}

